import { useEffect, useState } from "react";
import { getSettings, sendEmail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { PaperPlaneTilt, Eye, Code, UserCircle, ArrowBendUpLeft, Paperclip, X, FileText } from "@phosphor-icons/react";

const inputCls =
  "w-full rounded-none border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-[#2563EB] transition-colors duration-150";
const labelCls =
  "block text-[11px] tracking-[0.15em] uppercase text-[#52525B] font-semibold mb-2";

function parseRecipients(raw) {
  return raw
    .split(/[\n,;]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

const MAX_TOTAL_BYTES = 20 * 1024 * 1024; // 20MB total

function fmtSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function readAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || "";
      const base64 = String(result).split(",")[1] || "";
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Compose() {
  const [form, setForm] = useState({
    from_name: "",
    from_email: "",
    reply_to: "",
    recipients: "",
    subject: "",
    html_body: "",
  });
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    getSettings()
      .then((s) => {
        setForm((f) => ({
          ...f,
          from_name: s.default_from_name || "",
          from_email: s.default_from_email || "",
          reply_to: s.default_reply_to || "",
        }));
      })
      .catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const recipientList = parseRecipients(form.recipients);

  const totalAttachSize = attachments.reduce((n, a) => n + a.size, 0);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-selecting same file
    let running = totalAttachSize;
    const added = [];
    for (const file of files) {
      running += file.size;
      if (running > MAX_TOTAL_BYTES) {
        toast.error("Attachments exceed 20MB total limit");
        break;
      }
      try {
        const content = await readAsBase64(file);
        added.push({
          filename: file.name,
          content_type: file.type || "application/octet-stream",
          content,
          size: file.size,
        });
      } catch {
        toast.error(`Could not read ${file.name}`);
      }
    }
    if (added.length) setAttachments((prev) => [...prev, ...added]);
  };

  const removeAttachment = (idx) =>
    setAttachments((prev) => prev.filter((_, i) => i !== idx));

  const handleSend = async () => {
    if (!form.from_name.trim()) return toast.error("From name is required");
    if (!form.from_email.trim()) return toast.error("From email is required");
    if (recipientList.length === 0) return toast.error("Add at least one recipient");
    if (!form.subject.trim()) return toast.error("Subject is required");

    setSending(true);
    setResult(null);
    try {
      const res = await sendEmail({
        from_name: form.from_name,
        from_email: form.from_email,
        reply_to: form.reply_to,
        recipients: recipientList,
        subject: form.subject,
        html_body: form.html_body,
        text_body: form.html_body.replace(/<[^>]+>/g, " "),
        attachments: attachments.map((a) => ({
          filename: a.filename,
          content_type: a.content_type,
          content: a.content,
        })),
      });
      setResult(res);
      if (res.failed_count === 0) {
        toast.success(`Sent to ${res.sent_count} recipient(s)`);
      } else {
        toast.warning(`${res.sent_count} sent, ${res.failed_count} failed`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="compose-page">
      <PageHeader
        eyebrow="New message"
        title="Compose"
        subtitle="The inbox shows only your From-name. Replies route to your separate Reply-To address."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border-t border-[#E5E7EB]">
        {/* Form */}
        <div className="lg:col-span-2 p-10 border-r border-[#E5E7EB]">
          {/* Sender identity block */}
          <div className="border border-[#E5E7EB] bg-[#F9FAFB] p-6 mb-8">
            <div className="flex items-center gap-2 mb-5">
              <UserCircle size={18} weight="fill" color="#2563EB" />
              <span className="text-xs tracking-[0.2em] uppercase font-semibold">Sender identity</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className={labelCls}>From name (visible)</label>
                <input
                  data-testid="from-name-input"
                  className={inputCls}
                  placeholder="Acme Support"
                  value={form.from_name}
                  onChange={set("from_name")}
                />
              </div>
              <div>
                <label className={labelCls}>From email</label>
                <input
                  data-testid="from-email-input"
                  className={`${inputCls} font-mono`}
                  placeholder="noreply@yourdomain.com"
                  value={form.from_email}
                  onChange={set("from_email")}
                />
              </div>
            </div>
            <div className="mt-5 flex items-start gap-2 bg-white border border-[#E5E7EB] p-3">
              <ArrowBendUpLeft size={16} weight="bold" color="#2563EB" className="mt-0.5 shrink-0" />
              <div className="w-full">
                <label className={labelCls}>Reply-To (where replies go)</label>
                <input
                  data-testid="reply-to-input"
                  className={`${inputCls} font-mono`}
                  placeholder="me@mypersonal.com"
                  value={form.reply_to}
                  onChange={set("reply_to")}
                />
              </div>
            </div>
          </div>

          <div className="mb-6">
            <label className={labelCls}>Recipients</label>
            <textarea
              data-testid="recipients-input"
              className={`${inputCls} font-mono h-28 resize-none`}
              placeholder="one@example.com, two@example.com&#10;three@example.com"
              value={form.recipients}
              onChange={set("recipients")}
            />
            <div className="text-xs text-[#A1A1AA] mt-1.5">
              Separate with commas or new lines.{" "}
              <span className="text-[#2563EB] font-semibold" data-testid="recipient-count">
                {recipientList.length} recipient(s)
              </span>
            </div>
          </div>

          <div className="mb-6">
            <label className={labelCls}>Subject</label>
            <input
              data-testid="subject-input"
              className={inputCls}
              placeholder="Your subject line"
              value={form.subject}
              onChange={set("subject")}
            />
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className={labelCls}>Message (HTML supported)</label>
              <button
                data-testid="toggle-preview-btn"
                onClick={() => setShowPreview(!showPreview)}
                className="text-xs flex items-center gap-1.5 text-[#52525B] hover:text-[#2563EB] transition-colors duration-150 font-semibold"
              >
                {showPreview ? <Code size={14} /> : <Eye size={14} />}
                {showPreview ? "Edit HTML" : "Preview"}
              </button>
            </div>
            {showPreview ? (
              <div
                data-testid="html-preview"
                className="border border-[#E5E7EB] bg-white p-4 min-h-[200px] text-sm"
                dangerouslySetInnerHTML={{ __html: form.html_body || "<p style='color:#A1A1AA'>Nothing to preview</p>" }}
              />
            ) : (
              <textarea
                data-testid="body-input"
                className={`${inputCls} font-mono h-56 resize-none`}
                placeholder="<h1>Hello</h1><p>Write your message here...</p>"
                value={form.html_body}
                onChange={set("html_body")}
              />
            )}
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className={labelCls}>Attachments</label>
              <span className="text-xs text-[#A1A1AA] font-mono" data-testid="attachment-total-size">
                {fmtSize(totalAttachSize)} / 20 MB
              </span>
            </div>
            <label
              data-testid="attachment-dropzone"
              className="flex items-center justify-center gap-2 border border-dashed border-[#E5E7EB] bg-[#F9FAFB] px-4 py-6 text-sm text-[#52525B] cursor-pointer hover:border-[#2563EB] hover:text-[#2563EB] transition-colors duration-150"
            >
              <Paperclip size={16} weight="bold" />
              Click to attach files
              <input
                data-testid="attachment-input"
                type="file"
                multiple
                className="hidden"
                onChange={handleFiles}
              />
            </label>

            {attachments.length > 0 && (
              <div className="mt-3 border border-[#E5E7EB]" data-testid="attachment-list">
                {attachments.map((a, i) => (
                  <div
                    key={i}
                    data-testid={`attachment-item-${i}`}
                    className="flex items-center gap-3 px-3 py-2 border-b border-[#F3F4F6] last:border-0 text-sm"
                  >
                    <FileText size={16} weight="fill" color="#2563EB" className="shrink-0" />
                    <span className="flex-1 truncate">{a.filename}</span>
                    <span className="text-xs text-[#A1A1AA] font-mono shrink-0">{fmtSize(a.size)}</span>
                    <button
                      data-testid={`remove-attachment-${i}`}
                      onClick={() => removeAttachment(i)}
                      className="text-[#A1A1AA] hover:text-[#EF4444] transition-colors duration-150 shrink-0"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            data-testid="send-btn"
            onClick={handleSend}
            disabled={sending}
            className="rounded-none bg-[#09090B] text-white px-8 py-3.5 text-sm font-semibold flex items-center gap-2 hover:bg-[#2563EB] transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PaperPlaneTilt size={16} weight="fill" />
            {sending ? "Sending..." : `Send${recipientList.length ? ` to ${recipientList.length}` : ""}`}
          </button>
        </div>

        {/* Live inbox preview */}
        <div className="p-10 bg-[#F9FAFB]">
          <div className="text-[11px] tracking-[0.2em] uppercase text-[#52525B] font-semibold mb-4">
            How the inbox sees it
          </div>
          <div className="border border-[#E5E7EB] bg-white p-4" data-testid="inbox-preview">
            <div className="flex items-center gap-3 border-b border-[#F3F4F6] pb-3">
              <div className="w-9 h-9 bg-[#09090B] text-white flex items-center justify-center font-display font-bold">
                {(form.from_name || "?").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">
                  {form.from_name || "From name"}
                </div>
                <div className="text-xs text-[#A1A1AA] truncate">
                  {form.subject || "Subject line"}
                </div>
              </div>
            </div>
            <div className="text-xs text-[#A1A1AA] mt-3 leading-relaxed">
              The recipient's inbox list shows your <b className="text-[#09090B]">From-name</b> prominently.
              The actual address is only visible if they expand the header.
            </div>
          </div>

          {result && (
            <div className="mt-6 border border-[#E5E7EB] bg-white p-4" data-testid="send-result">
              <div className="text-[11px] tracking-[0.2em] uppercase text-[#52525B] font-semibold mb-3">
                Last send result
              </div>
              <div className="font-display font-extrabold text-3xl">
                {result.sent_count}
                <span className="text-[#A1A1AA] text-lg">/{result.total} sent</span>
              </div>
              {result.recipients?.filter((r) => r.status === "failed").length > 0 && (
                <div className="mt-3 space-y-1">
                  {result.recipients
                    .filter((r) => r.status === "failed")
                    .map((r, i) => (
                      <div key={i} className="text-xs text-[#EF4444] font-mono break-all">
                        {r.email}: {r.error}
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
